import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import uuidv1 from 'uuid/v1';
import _ from 'lodash';
import { compose } from 'recompose';
import moment from 'moment';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import { Modal, Form, Icon } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import { authService } from '../../utils/auth-service';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';

const INITIAL_STATE = {
  success: false,
  storyDescription: null,
  assetURL: null,
  assetType: 'STORY',
  publishedDate: null,
  platform: 'INSTAGRAM',
};

const requiredStyle = {
  color: '#db2828',
};

class AddStoryModal extends Component {
  constructor() {
    super();

    this.state = INITIAL_STATE;

    this.restClient = axios.create({
      timeout: 5000,
      headers: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });
  }

  toggler = () => {
    const { popupToggleHandler } = this.props;
    this.setState(INITIAL_STATE);
    popupToggleHandler();
  }

  addStory = () => {
    const { storyDescription, platform, publishedDate, assetURL, assetType } = this.state;
    const {
      successHandler, campaignId, discussionId, discussionName, jsonAPIClient,
    } = this.props;
    const uuid = uuidv1();
    const dyzioURL = `http://app.dyzio.co/assets/${uuid}`;
    const extraData = {
      text: storyDescription,
    };
    const trackedAsset = jsonAPIClient.create('trackedAssets');
    trackedAsset.set('assetId', uuid);
    trackedAsset.set('assetURL', assetURL || dyzioURL);
    trackedAsset.set('extraData', extraData);
    trackedAsset.set('publishedDate', publishedDate);
    trackedAsset.set('user', {
      type: 'users',
      id: authService.uid(),
    });
    trackedAsset.set('campaign', {
      type: 'campaigns',
      id: campaignId,
    });
    trackedAsset.set('discussion', {
      type: 'discussions',
      id: discussionId,
    });

    trackedAsset.set('discussionName', discussionName);
    trackedAsset.set('platform', platform);
    trackedAsset.set('assetType', assetType);
    trackedAsset.set('createdSource', 'CAMPAIGN');

    return trackedAsset.sync()
      .then(response => {
        const newTrackedAsset = response.toJSON();
        const mainUrl = `${process.env.REACT_APP_API_BASE_URL}`;
        const relUrl = `${mainUrl}/discussions/${discussionId}/relationships/trackedAssets`;
        this.restClient.post(relUrl, {
          data: {
            type: 'trackedAssets',
            id: newTrackedAsset.id,
          },
        }).then(async () => {
          await this.restClient.patch(`${mainUrl}/discussions/${discussionId}`, {
            data: {
              attributes: {
                hasPosted: true
              },
            },
          });
          successHandler(newTrackedAsset);
          this.toggler();
        });
      })
      .catch(e => {
        console.log('Error', e);
      });
  }

  handleChange = (e, { name, value }) => {
    const newState = { [name]: value };
    if (name === 'platform') {
      newState.assetType = null;
    }
    if (name === 'platform' && value === 'INSTAGRAM') {
      newState.assetType = 'STORY';
    }
    if (name === 'platform' && value === 'BLOG') {
      newState.assetType = 'TEXT';
    }
    this.setState(newState);
  };

  handleDateInputChange = (date, event) => {
    event.preventDefault();
    const publishedDate = date.set('hour', 15).format();
    this.setState({
      publishedDate,
    });
  }

  render() {
    const { storyDescription, assetURL, platform, publishedDate: publishedDateAsString, assetType } = this.state;
    const { popupToggleHandler } = this.props;

    let saveDisabled = !(storyDescription && publishedDateAsString && assetType);

    if ((platform !== 'INSTAGRAM' && platform !== 'SNAPCHAT') && !assetURL) {
      saveDisabled = true;
    }

    const publishedDate = publishedDateAsString ? moment(publishedDateAsString) : '';

    const options = [
      {
        text: 'Instagram Story ',
        value: 'INSTAGRAM',
      },
      {
        text: 'Snapchat',
        value: 'SNAPCHAT',
        assetTypes: [
          { text: 'Video', value: 'VIDEO' },
          { text: 'Image', value: 'IMAGE' },
        ],
      },
      {
        text: 'VK',
        value: 'VK',
        assetTypes: [
          { text: 'Video', value: 'VIDEO' },
          { text: 'Image', value: 'IMAGE' },
          { text: 'Text', value: 'TEXT' },
        ],
      },
      {
        text: 'Blog Post',
        value: 'BLOG',
        assetTypes: [
          { text: 'Text', value: 'TEXT' },
        ],
      },
    ];

    const selectedPlatform = _.find(options, ['value', platform]);

    return (
      <DyzioModal
        onClose={popupToggleHandler}
        headerText="add other posts & stories"
        headerDescription="Manually add posts and data from social networks and content, not yet supported by dyzio."
        size="small"
        hasActionButton
        actionButtonContent="Add"
        actionButtonColor="pink"
        actionButtonIcon={<Icon name="add" />}
        onActionButtonClick={this.addStory}
        shouldDisableActionButton={saveDisabled}
      >
        <Modal.Content textAlign="left">

          <Form>

            <Form.Select
              label="Platform"
              options={options}
              value={platform}
              name="platform"
              onChange={this.handleChange}
            />

            {
              selectedPlatform && selectedPlatform.assetTypes &&
              <Form.Select
                required
                label="Asset type"
                options={selectedPlatform.assetTypes}
                value={assetType}
                name="assetType"
                onChange={this.handleChange}
                placeholder="Please select"
              />
            }


            <Form.Input
              required
              placeholder="a description for reporting"
              label="Description"
              value={storyDescription}
              name="storyDescription"
              onChange={this.handleChange}
            />

            {
              platform !== 'INSTAGRAM' && platform !== 'SNAPCHAT' &&
              <Form.Input
                required
                type="text"
                placeholder="link to blog post or social post"
                label="Link to post"
                value={assetURL}
                name="assetURL"
                onChange={this.handleChange}
              />
            }


            <div className="field">
              <label htmlFor="publishedDate">
                Published date <span style={requiredStyle}>*</span>
                <DatePicker
                  onChange={this.handleDateInputChange}
                  id="publishedDate"
                  placeholderText={`e.g. ${moment().format('DD/MM/Y')}`}
                  selected={publishedDate}
                  dateFormat="DD/MM/Y"
                />
              </label>
            </div>
          </Form>
        </Modal.Content>
      </DyzioModal>
    );
  }
}

AddStoryModal.propTypes = {
  popupToggleHandler: PropTypes.func.isRequired,
  successHandler: PropTypes.func.isRequired,
  campaignId: PropTypes.string.isRequired,
  discussionId: PropTypes.string.isRequired,
  discussionName: PropTypes.string.isRequired,
  jsonAPIClient: PropTypes.object.isRequired,
};

export default compose(
  hasJSONAPIClient,
)(AddStoryModal);



/** WEBPACK FOOTER **
 ** ./src/components/add-story-modal/index.js
 **/